/*
 * Copyright (C) 2013 Spanning Cloud Apps.  All rights reserved.
 */
import permissions from '../../../../common/constants/permissions';

import './history.tpl.html';

/**
 * Module for the api history view.
 */
angular
  .module('admin.api.history', ['services.permissions'])
  .constant('API_CLS', {
    PROGRESS_USED_LEFT: 'sbsf-admin-api-used-left',
    PROGRESS_USED_RIGHT: 'sbsf-admin-api-used-right'
  })
  .controller(
    'ApiHistoryCtrl',
    function (
      $scope,
      authFactory,
      httpApi,
      ALERT_EVENTS,
      $filter,
      TIME_INTERVALS,
      API_TYPES,
      API_CLS,
      API_VIEWS,
      VIEW_STATES,
      permissionService
    ) {
      const i18n = $filter('i18n');

      const config = ($scope.config = $scope.config || {});
      config.api = config.api || API_TYPES.REST_API;
      $scope.API_TYPES = API_TYPES;
      $scope.API_VIEWS = API_VIEWS;
      $scope.VIEW_STATES = VIEW_STATES;

      const DAY = 'day';
      const HOUR = 'hour';

      authFactory.getSignedRequest().then(function (userInfo) {
        $scope.isExternalUser = userInfo.loginType.scope === 'EXTERNAL';
      });
      /**
       * Function used to load the api count data from the httpApi service. It's published in the scope so we can call it
       * when testing.
       */
      $scope.loadApiUsageHistoryData = function () {
        return permissionService
          .hasPermission(permissions.API_USAGE)
          .then((hasPermission) => {
            return hasPermission ? httpApi.getApiUsageHistory(config.api, $scope.timeInterval) : null;
          })
          .then((historyObj) => {
            $scope.data = historyObj ? formatData(historyObj.rows, $scope.timeInterval) : null;
          })
          .catch(() => {
            $scope.$emit(ALERT_EVENTS.ERROR.eventName, $filter('i18n')('LOAD_API_USAGE_FAILED'));
          });
      };

      /**
       * Change time interval handler
       */
      $scope.changeTimeInterval = () => {
        if ($scope.timeInterval === TIME_INTERVALS.HOURLY) {
          $scope.timeInterval = TIME_INTERVALS.DAILY;
          $scope.changeTimeIntervalButtonMessage = i18n('SWITCH_TO_HOURLY_TIME_INTERVAL');
        } else {
          $scope.timeInterval = TIME_INTERVALS.HOURLY;
          $scope.changeTimeIntervalButtonMessage = i18n('SWITCH_TO_DAILY_TIME_INTERVAL');
        }
        loadApiUsageWithMaskControl();
      };

      if (config.api === API_TYPES.BULK_API) {
        $scope.changeTimeInterval();
      } else {
        loadApiUsageWithMaskControl();
      }

      /**
       * Get the percentage of calls used as an integer.
       *
       * @returns {int} The percent of calls used.
       */
      $scope.getUsedPct = function () {
        var pct;
        if ($scope.limit === 0) {
          pct = 0;
        } else {
          // never exceed 100%
          pct = Math.min(parseInt(($scope.used / $scope.limit) * 100, 10), 100);
        }

        return pct;
      };

      /**
       * Returns the style attributes to apply to the progress
       * text container.
       *
       * @returns {object} An object with the padding property to
       * center the progress container text.
       */
      $scope.getApiTextPadding = function () {
        var usedPct = $scope.getUsedPct(),
          position,
          positionPct;
        // For the 100% special case set a static position.
        // Otherwise depending on whether the parent container will
        // be over 50% complete or less we decide
        // which side to calculate the position so
        // the text appears the side it's placed in.
        if (usedPct >= 100) {
          position = { right: '18%' };
        } else if ($scope.getUsedPct() >= 50) {
          positionPct = 100 - $scope.getUsedPct() + 10;
          position = { right: positionPct + '%' };
        } else {
          positionPct = $scope.getUsedPct() + 10;
          position = { left: positionPct + '%' };
        }
        return position;
      };

      /**
       * Get the percentage of calls used for the current day.
       *
       * @returns {object} An object with a single property width that has
       * a value of a percentage.
       */
      $scope.getUsedWidth = function () {
        var pct = $scope.getUsedPct();
        return { width: pct + '%', 'max-width': pct + '%' };
      };

      /**
       * Returns the class to apply to the used progress bar based on the current
       * api calls used.  It positions the text within the progress bar.
       *
       * @returns {string} A class to apply to the used progress bar.
       */
      $scope.getApiTextCls = function () {
        var cls;
        if ($scope.getUsedPct() >= 50) {
          cls = API_CLS.PROGRESS_USED_LEFT;
        } else {
          cls = API_CLS.PROGRESS_USED_RIGHT;
        }
        return cls;
      };

      /**
       * Load api usage data with loading mask control
       */
      function loadApiUsageWithMaskControl() {
        $scope.showLoadingMask = true;
        $scope.loadApiUsageHistoryData().finally(function () {
          $scope.showLoadingMask = false;
        });
      }

      /**
       * Returns the history record whose date matches the specified date moment.
       *
       * @param {Array} data - The array of history records
       * @param {moment} dateMoment - The moment to look for
       *
       * @returns {Object} The matching history record, or null if no matching history record was found.
       */
      function findRecord(data, dateMoment, timeInterval) {
        let momentIntervalKey = timeInterval === TIME_INTERVALS.HOURLY ? HOUR : DAY;
        return _.find(data, function (dayInfo) {
          return dateMoment.isSame(dayInfo.date, momentIntervalKey);
        });
      }

      /**
       * Looks for missing dates in the data array and fills them in.
       *
       * @param {Array} data - The array of history records
       *
       * @return {Array} The updated data
       */
      function fillMissingDates(data, timeInterval) {
        var newData = [],
          firstInfo,
          prevInfo,
          lastInfo,
          nextMoment,
          lastMoment,
          info;

        let momentIntervalKey = timeInterval === TIME_INTERVALS.HOURLY ? HOUR : DAY;

        // We'll build a new data array, adding any missing values.
        firstInfo = data[0];
        lastInfo = data[data.length - 1];
        lastMoment = moment(lastInfo.date);

        // Add the first record
        newData.push(firstInfo);
        prevInfo = firstInfo;

        // Loop a day at a time until we hit the last date
        nextMoment = moment(firstInfo.date).add(1, momentIntervalKey);
        while (nextMoment.isBefore(lastMoment)) {
          // See if there is a record for this date
          info = findRecord(data, nextMoment, timeInterval);
          if (!info) {
            // Add a new record. We'll assume no calls and we'll carry over the previous limit.
            info = {
              date: nextMoment.clone().toDate(),
              count: 0,
              limit: prevInfo.limit,
              sfOrgId: prevInfo.sfOrgId
            };
          }

          newData.push(info);
          prevInfo = info;
          nextMoment.add(1, momentIntervalKey);
        }

        // Add the last record
        newData.push(lastInfo);

        return newData;
      }

      /**
       * Helper function to format the history usage data to be consumable by the line chart.
       *
       * @param {Array} data - The data set to be parsed.
       *
       * @return {Array} The formatted data object. Each array entry contains the date, count, and limit
       */
      function formatData(data, timeInterval) {
        let momentIntervalStartKey = timeInterval === TIME_INTERVALS.HOURLY ? HOUR : DAY;
        // Convert dates to interval start UTC.
        _.each(data, function (dayInfo) {
          dayInfo.date = moment(dayInfo.date).utc().startOf(momentIntervalStartKey).toDate();
        });

        // sort in increasing date order
        data = _.sortBy(data, 'date');

        // Look for missing dates. We'll build a new data array, adding any missing values.
        if (data && data.length > 1) {
          data = fillMissingDates(data, timeInterval);
        }

        return data;
      }
    }
  )
  .directive('apiHistory', function () {
    return {
      template: '<div ng-include="templateUrl"></div>',
      scope: {
        config: '=',
        templateUrl: '=',
        isChatter: '=',
        limitStr: '=',
        accountStr: '=',
        showView: '&',
        used: '=',
        limit: '='
      },
      controller: 'ApiHistoryCtrl'
    };
  })
  .directive('adminApiUsageChart', function ($compile, $filter, TIME_INTERVALS) {
    return {
      restrict: 'E',
      scope: {
        apidata: '=',
        timeInterval: '='
      },
      link: function (scope, element) {
        var DOT_RADIUS = 4,
          // Set the size of the fixed elements
          VIEW_WD = 560,
          VIEW_HT = 145,
          VIEW_MARGIN_RIGHT = 40,
          // The text at the top showing the number of days
          COUNT_HT = 20,
          // The legend at the bottom
          LEGEND_HT = 20,
          LEGEND_MARGIN_TOP = 10,
          // The axes
          YAXIS_TITLE_WD = 30,
          YAXIS_WD = 40,
          XAXIS_HT = 20,
          // The chart takes up the remaining space
          CHART_WD = VIEW_WD - YAXIS_TITLE_WD - YAXIS_WD - VIEW_MARGIN_RIGHT,
          CHART_HT = VIEW_HT - COUNT_HT - XAXIS_HT - LEGEND_HT,
          // The types of lines we display in the history
          LINE_TYPE = Object.freeze({
            COUNT: 'count',
            LIMIT: 'limit'
          }),
          i18n = $filter('i18n'),
          pluralize = $filter('pluralize'),
          svg,
          countBox,
          chartBox,
          xAxisBox,
          yAxisBox,
          yAxisTitleBox,
          legendBox,
          xScale,
          yScale,
          xAxis,
          yAxis,
          newSvg;

        /**
         * Sets up the layout and creates the SVG elements for drawing the chart.
         */
        function init() {
          var countX,
            countY,
            chartX,
            chartY,
            yAxisTitleX,
            yAxisTitleY,
            yAxisX,
            yAxisY,
            xAxisX,
            xAxisY,
            legendX,
            legendY;

          /**
           * Creates an SVG container
           *
           * @param {String} name - The name of the container
           * @param {Number} x - The x coordinate of the container
           * @param {Number} y - The y coordinate of the container
           *
           * @return {Object} The new container
           */
          function createContainer(name, x, y) {
            return svg
              .append('g')
              .attr('transform', 'translate(' + x + ',' + y + ')')
              .attr('class', name);
          }

          // Remove any existing element. We'll recreate it.
          element.find('svg').remove();
          svg = d3
            .select(element[0])
            .append('svg')
            .attr('viewBox', '0 0 ' + VIEW_WD + ' ' + VIEW_HT)
            .attr('height', '100%')
            .attr('width', '100%')
            .attr('preserveAspectRatio', 'xMidYMid meet');

          // Create containers for each part of the view so we don't have to worry about translations when we're
          // drawing inside each container.

          // Position the chart.
          chartX = YAXIS_TITLE_WD + YAXIS_WD;
          chartY = COUNT_HT;
          chartBox = createContainer('chart', chartX, chartY);

          // Position the label showing the number of backups
          countX = chartX;
          countY = 0;
          countBox = createContainer('count', countX, countY);

          // Position the Y axis title
          yAxisTitleX = 0;
          yAxisTitleY = chartY;
          yAxisTitleBox = createContainer('axis-title y-axis', yAxisTitleX, yAxisTitleY);

          // Position the Y axis
          yAxisX = yAxisTitleX + YAXIS_TITLE_WD;
          yAxisY = chartY;
          yAxisBox = createContainer('axis y-axis', yAxisX, yAxisY);

          // Position the X axis
          xAxisX = chartX;
          xAxisY = chartY + CHART_HT;
          xAxisBox = createContainer('axis x-axis', xAxisX, xAxisY);

          // Position the legend
          legendX = chartX;
          legendY = xAxisY + XAXIS_HT + LEGEND_MARGIN_TOP;
          legendBox = createContainer('legend', legendX, legendY);
        }

        /**
         * Creates the X axis
         *
         * @param {Object} data - The history info
         */
        function createXAxis(data, timeInterval) {
          xScale = d3
            .scaleUtc()
            .domain(
              d3.extent(data, function (dayInfo) {
                return dayInfo.date;
              })
            )
            .range([0, CHART_WD])
            .nice(d3.utcDay);

          xAxis = d3
            .axisBottom(xScale)
            // Show ticks on the day
            .ticks(d3.utcDay.every(1))
            .tickFormat(function (date) {
              return getTickTitle(date, timeInterval);
            });
        }

        /**
         * Creates the Y axis
         *
         * @param {Object} data - The history info
         */
        function createYAxis(data) {
          var yMax = d3.max(data, function (dayInfo) {
            return Math.max(dayInfo.count, dayInfo.limit);
          });

          yScale = d3
            .scaleLinear()
            // We always want to show 0 at the bottom of the y axis
            .domain([0, yMax])
            .range([CHART_HT, 0])
            .nice();

          yAxis = d3.axisLeft(yScale).ticks(5);
        }

        /**
         * Draws the chart background
         *
         * @param {Object} data - The history info
         */
        function drawBackground(data) {
          chartBox.append('rect').attr('class', 'chart-background').attr('width', CHART_WD).attr('height', CHART_HT);

          // If there is no data show a message here
          if (data.length === 0) {
            chartBox
              .append('text')
              .attr('class', 'no-data')
              .attr('text-anchor', 'middle')
              .attr('x', CHART_WD / 2)
              .attr('y', CHART_HT / 2)
              .text(i18n('NO_ACTIVITY'));
          }
        }

        /**
         * Draws the x axis, grid lines, and tick marks
         */
        function drawXAxis() {
          /**
           * Creates the vertical grid lines across the chart.
           *
           * @param {Object} g - the SVG container
           */
          function makeXAxisGrid(g) {
            var xAxisGrid = d3
              .axisBottom(xScale)
              // Show ticks on the day
              .ticks(d3.utcDay.every(1))
              .tickSize(CHART_HT)
              .tickFormat('');

            g.call(xAxisGrid);
          }

          // Draw the tick marks
          xAxisBox.append('g').attr('class', 'axis').call(xAxis);

          // Draw the vertical grid lines
          chartBox.append('g').attr('class', 'grid').call(makeXAxisGrid);
        }

        /**
         * Draws the y axis, grid lines, and tick marks
         */
        function drawYAxis() {
          /**
           * Creates the horizontal grid lines across the chart.
           *
           * @param {Object} g - the SVG container
           */
          function makeYAxisGrid(g) {
            var yAxisGrid = d3.axisLeft(yScale).ticks(5).tickSize(-CHART_WD).tickFormat('');

            g.call(yAxisGrid);
          }

          // Draw the y axis and tick marks.
          yAxisBox
            .append('g')
            .attr('class', 'axis')
            // A left axis is drawn to the left of its position so we move it all the way to the right
            .attr('transform', 'translate(' + YAXIS_WD + ',0)')
            .call(yAxis);

          // Draw the horizontal grid lines
          chartBox.append('g').attr('class', 'grid').call(makeYAxisGrid);

          // Draw the rotated title
          yAxisTitleBox
            .append('text')
            .attr('class', 'capitalize')
            .attr('text-anchor', 'middle')
            .attr('transform', 'translate(' + YAXIS_TITLE_WD / 2 + ',' + CHART_HT / 2 + ') rotate(-90)')
            .text(i18n('CALL_COUNT'));
        }

        /**
         * Draws the daily API call count or limit line.
         *
         * @param {Object} data - The history info
         * @param {String} lineType - The type of line we're drawing
         */
        function drawCounts(data, lineType, timeInterval) {
          var path, counts;

          path = d3
            .line()
            .x(function (dayInfo) {
              return xScale(dayInfo.date);
            })
            .y(function (dayInfo) {
              return yScale(dayInfo[lineType]);
            });

          // Add the line graph
          counts = chartBox.append('g').attr('class', lineType + 's');

          counts.append('path').datum(data).attr('d', path).attr('class', lineType);

          // Add the dots
          counts
            .selectAll('circle')
            .data(data)
            .enter()
            .append('circle')
            .attr('class', lineType)
            .attr('cx', function (dayInfo) {
              return xScale(dayInfo.date);
            })
            .attr('cy', function (dayInfo) {
              return yScale(dayInfo[lineType]);
            })
            .attr('r', DOT_RADIUS)
            .attr('uib-tooltip-html', function (dayInfo) {
              var tooltip = '',
                capitalize = '',
                countText;

              // Show the full date, like 'Thu, Sep 4 1986'
              // TODO Use locale-specific format? Moment doesn't have one that includes the abbreviated day without
              // the time.
              tooltip += '<div>' + getTooltipDateText(dayInfo.date, timeInterval) + '</div>';

              // Show the property value.
              if (lineType === LINE_TYPE.COUNT) {
                countText = pluralize(dayInfo[lineType], 'API_CALL', 'API_CALLS');
              } else {
                countText = i18n('N_CALL_LIMIT', [dayInfo[lineType]]);
                capitalize = ' class="capitalize"';
              }
              tooltip += '<div' + capitalize + '>' + countText + '</div>';

              // UI Bootstrap 0.14.3 requires HTML to be a string inside a string.
              return "'" + tooltip + "'";
            })
            .attr('tooltip-append-to-body', true);
        }

        /**
         * Draws the number of days
         *
         * @param {Object} data - The history
         */
        function drawIntervals(data, timeInterval) {
          // Show the number of days
          var countText;

          if (data.length === 0) {
            countText = i18n('NO_ACTIVITY');
          } else {
            countText = getCountText(data, timeInterval);
          }

          countBox
            .append('text')
            .attr('class', 'primary-font-light capitalize sbsf-last-backups')
            .attr('x', 0)
            .attr('y', COUNT_HT / 2)
            .text(countText);
        }

        /**
         * Draws the legend for the chart.
         */
        function drawLegend() {
          var LINE_LENGTH = 30,
            LABEL_LEFT_MARGIN = 5,
            COUNT_LABEL_WIDTH = 40,
            DOT_Y = 0,
            LABEL_Y = 4,
            LINE_Y = 0,
            DOT_OFFSET_X = LINE_LENGTH / 2,
            callsLineX,
            callsDotX,
            callsLabelX,
            limitLineX,
            limitDotX,
            limitLabelX;

          // Show the call count
          callsLineX = 0;
          legendBox
            .append('line')
            .attr('class', 'count')
            .attr('x1', callsLineX)
            .attr('y1', LINE_Y)
            .attr('x2', callsLineX + LINE_LENGTH)
            .attr('y2', LINE_Y);

          callsDotX = callsLineX + DOT_OFFSET_X;
          legendBox
            .append('circle')
            .attr('class', 'count')
            .attr('cx', callsDotX)
            .attr('cy', DOT_Y)
            .attr('r', DOT_RADIUS);

          callsLabelX = callsLineX + LINE_LENGTH + LABEL_LEFT_MARGIN;
          legendBox
            .append('text')
            .attr('class', 'primary-font-light capitalize')
            .attr('x', callsLabelX)
            .attr('y', LABEL_Y)
            .text(i18n('CALLS'));

          // Show the limit
          limitLineX = callsLabelX + COUNT_LABEL_WIDTH;
          legendBox
            .append('line')
            .attr('class', 'limit')
            .attr('x1', limitLineX)
            .attr('y1', LINE_Y)
            .attr('x2', limitLineX + LINE_LENGTH)
            .attr('y2', LINE_Y);

          limitDotX = limitLineX + DOT_OFFSET_X;
          legendBox
            .append('circle')
            .attr('class', 'limit')
            .attr('cx', limitDotX)
            .attr('cy', DOT_Y)
            .attr('r', DOT_RADIUS);

          limitLabelX = limitLineX + LINE_LENGTH + LABEL_LEFT_MARGIN;
          legendBox
            .append('text')
            .attr('class', 'primary-font-light capitalize')
            .attr('x', limitLabelX)
            .attr('y', LABEL_Y)
            .text(i18n('LIMIT'));
        }

        /**
         * Rebuild chart
         * @param apidata - api data
         * @param timeInterval - time interval type
         */
        function rebuild(apidata, timeInterval) {
          init();
          createXAxis(apidata, timeInterval);
          createYAxis(apidata);
          drawBackground(apidata);

          // Only draw these elements if there is at least one data point.
          if (apidata.length) {
            drawYAxis();
            drawXAxis();
            drawCounts(apidata, LINE_TYPE.COUNT, timeInterval);
            drawCounts(apidata, LINE_TYPE.LIMIT, timeInterval);
            drawLegend();
            drawIntervals(apidata, timeInterval);
          }

          // Since we are using a directive for the tooltips
          // we will need to recompile those elements.
          newSvg = element.find('svg').clone();
          newSvg = $compile(newSvg)(scope);
          element.find('svg').replaceWith(newSvg);
        }

        /**
         * get text of chart title
         * @param data - data of chart
         * @param timeInterval - time interval type
         * @returns {*} - text
         */
        function getCountText(data, timeInterval) {
          return timeInterval === TIME_INTERVALS.HOURLY
            ? pluralize(data.length, 'LAST_HOUR', 'LAST_N_HOURS')
            : pluralize(data.length, 'LAST_DAY', 'LAST_N_DAYS');
        }

        /**
         * Get text of tick on x axis
         * @param date - date of chart
         * @param timeInterval - time interval type
         * @returns {*} text
         */
        function getTickTitle(date, timeInterval) {
          const format = timeInterval === TIME_INTERVALS.HOURLY ? 'h:mm A' : 'MMM D';
          return moment(date).format(format);
        }

        /**
         * Get text of point tooltip date
         * @param date - date of point
         * @param timeInterval - time interval type
         * @returns {*} text
         */
        function getTooltipDateText(date, timeInterval) {
          const format = timeInterval === TIME_INTERVALS.HOURLY ? 'h:mm A ddd, MMM D' : 'ddd, MMM D YYYY';
          return moment(date).format(format);
        }

        scope.$watch('apidata', function (newVal) {
          if (!newVal) {
            return;
          }
          rebuild(newVal, scope.timeInterval);
        });
      }
    };
  });
